import { aceSwiperSlots } from "./ace-swiper.get-names";
import { getAceSwiperNames } from "./ace-swiper.get-names";
import { h, forceUpdate } from "@stencil/core";
import EmblaCarousel from "embla-carousel";
import AutoPlay from "embla-carousel-autoplay";
import { JsonParser, getTranslation, getWindowWidth, debounce, throttle, getProperty, ResizeListener, hasSlot, } from "../../utils";
import { aceSwiperCssVariables } from "./ace-swiper.get-names";
const emblaOptions = {
    align: 'start',
    loop: false,
    dragFree: true,
    containScroll: 'trimSnaps',
};
const DEFAULT_TRANSLATIONS = {
    href_button: 'Alles ansehen',
};
export class AceSwiper {
    constructor() {
        this.headlineTag = 'h2';
        this.autoplay = false;
        this.delay = 5000;
        // This property only works when the items within the swiper are set to full-width – but the full-width should be disabled once it should not fill space anymore.
        this.fillSpace = false;
        this.dark = false;
        this.hideIfEmpty = true;
        this.loading = false;
        this.withControls = true;
        this.showControls = false;
        this.numberOfCards = 0;
        this.lastSlideInView = false;
        this.observerLock = false;
        this._loading = false;
        /**
         *
         * @private
         */
        this.calculateIfWeHaveToShowControls = ({ windowWidth }) => {
            if (this.withControls) {
                if (this.numberOfCards <= 1 || (this.fillSpace && this.numberOfCards > 1 && this.numberOfCards < 5)) {
                    this.showControls = false;
                }
                else {
                    const widthWithoutGutter = windowWidth - 24 * 3; // 16*3 due to some inner gutter – it is not pixel perfect, but rather an approximation.
                    const cardWidth = this.cards[0].clientWidth;
                    const allCardsWidth = this.numberOfCards * cardWidth + this.numberOfCards * 16;
                    this.showControls = widthWithoutGutter < allCardsWidth;
                }
            }
        };
        this.handleNextClick = () => {
            this.emblaApi && this.emblaApi.scrollNext();
        };
        this.handlePrevClick = () => {
            this.emblaApi && this.emblaApi.scrollPrev();
        };
        this.renderCarousel = () => {
            const cardLength = this.cards ? this.cards.length : null;
            const mustFillSpace = this.fillSpace && cardLength && cardLength > 1 && cardLength < 5;
            let cssClasses = {
                [this.names.CLASS.CAROUSEL]: true,
                [this.names.CLASS.CAROUSEL____FILL]: mustFillSpace,
                [this.names.CLASS.CAROUSEL____FILL_1_OF_2]: mustFillSpace && cardLength === 2,
                [this.names.CLASS.CAROUSEL____FILL_1_OF_3]: mustFillSpace && cardLength === 3,
                [this.names.CLASS.CAROUSEL____FILL_1_OF_4]: mustFillSpace && cardLength === 4,
            };
            /* Apply grid layout for desktop devices, if there are less than 5 cards and grid is set to true */
            if (mustFillSpace) {
                this.showControls = false;
            }
            return (h("div", { class: cssClasses }, h("div", { class: this.names.CLASS.CAROUSEL__VIEWPORT, ref: el => (this.carouselViewport = el) }, h("div", { class: { [this.names.CLASS.CAROUSEL__CONTAINER]: true, [this.names.CLASS.CAROUSEL__CONTAINER____AUTOPLAY]: this.autoplay } }, h("slot", null), this.renderLoader()))));
        };
    }
    componentWillLoad() {
        var _a;
        this.names = getAceSwiperNames((_a = this.hostElement) === null || _a === void 0 ? void 0 : _a.id);
        this.parseLoading();
        this.parseTranslations();
        this.parseGap();
    }
    // Parse cards slot content when component renders for the first time
    componentDidLoad() {
        this.initEmbla();
        this.setupObserver();
    }
    disconnectedCallback() {
        this.observer.disconnect();
        ResizeListener.getInstance().unsubscribe(this.calculateIfWeHaveToShowControls);
    }
    parseLoading() {
        this._loading = this.loading;
    }
    parseGap() {
        this._customGap = new JsonParser(this.customGap, {}).getValue();
    }
    parseTranslations() {
        this._translations = new JsonParser(this.translations, {}).getValue();
    }
    // We need to manually observe changes in the slot to trigger re-render when the cards changes
    // This is a workaround for the issue that elements are rendered outside of slot when shadow DOM is disabled
    // https://github.com/ionic-team/stencil/issues/2641
    setupObserver() {
        const config = { childList: true };
        const callback = () => {
            // for (const mutation of mutationsList) {
            // 	if (mutation.type === 'childList') {
            // 	}
            // }
            forceUpdate(this.hostElement);
            // Also, set the coming from observer to false so the slidesMoved event is not triggered.
            this.observerLock = true;
            // Literally right after the rerender to ensure the cards are there.
            setTimeout(() => {
                this.cards = this.emblaApi.slideNodes();
                this.numberOfCards = this.cards.length;
                this.calculateIfWeHaveToShowControls({ windowWidth: getWindowWidth() });
                this.emblaApi.reInit();
            }, 10);
            // Set the coming from observer stuff back to false after a short delay to ensure the slidesMoved event can be triggered again.
            setTimeout(() => {
                this.observerLock = false;
            }, 750);
        };
        this.observer = new MutationObserver(callback);
        this.observer.observe(this.hostElement, config);
    }
    getStandardEventObject(e) {
        const slidesInView = e.slidesInView();
        let lastSlideInView, numberOfSlidesInView = 0, firstSlideInView;
        if (Array.isArray(slidesInView)) {
            numberOfSlidesInView = slidesInView.length;
            lastSlideInView = slidesInView[numberOfSlidesInView - 1];
            firstSlideInView = slidesInView[0];
        }
        return {
            canScrollPrev: e.canScrollPrev(),
            canScrollNext: e.canScrollNext(),
            firstSlideInView: firstSlideInView,
            lastSlideInView: lastSlideInView,
            swiperId: this.names.ID.ROOT,
            numberOfSlidesInView: numberOfSlidesInView,
            totalSlides: this.numberOfCards
        };
    }
    /**
     * @param e
     * @private
     */
    handleSlidesMovement(e) {
        const eventDetail = this.getStandardEventObject(e);
        // If this event is triggered on first load, do not continue as the slides didn't really move.
        if (this.lastSlideInView === false) {
            this.lastSlideInView = eventDetail.lastSlideInView;
            return;
        }
        if (!this.observerLock && eventDetail.lastSlideInView !== this.lastSlideInView) {
            this.lastSlideInView = eventDetail.lastSlideInView;
            this.slidesMoved.emit(eventDetail);
        }
    }
    handleSwiperScrolled(e) {
        this.swiperScrolled.emit(this.getStandardEventObject(e));
    }
    /**
     *
     * @param e
     * @private
     */
    handleSwiperInit(e) {
        this.calculateIfWeHaveToShowControls({ windowWidth: getWindowWidth() });
        const eventDetail = this.getStandardEventObject(e);
        this.swiperInitialised.emit(eventDetail);
    }
    /**
     *
     * @private
     */
    handleContentChange(e) {
        // if it's loading do not throw event: the loader card is detected as a slide and therefore a change in slides.
        if (this._loading) {
            return;
        }
        this.cards = this.emblaApi.slideNodes();
        this.numberOfCards = this.cards.length;
        this.calculateIfWeHaveToShowControls({ windowWidth: getWindowWidth() });
        const eventDetail = this.getStandardEventObject(e);
        this.emblaApi.reInit();
        this.slidesChanged.emit(eventDetail);
    }
    /**
     *
     * @private
     */
    initEmbla() {
        // If Embla is already initialized, do nothing
        if (this.emblaApi) {
            return;
        }
        emblaOptions.slides = `.${this.names.CLASS.CAROUSEL__CONTAINER} > :not(script):not(link):not(style):not(.${this.names.CLASS.CAROUSEL__LOADER____EMPTY})`; // Select all children of the container that are not script or link elements
        let plugins = [];
        if (this.autoplay) {
            emblaOptions.loop = true;
            let autoPlayConfig = {
                delay: this.delay,
            };
            plugins.push(AutoPlay(autoPlayConfig));
        }
        this.emblaApi = EmblaCarousel(this.carouselViewport, emblaOptions, plugins);
        this.emblaApi.on('slidesInView', debounce(this.handleSlidesMovement.bind(this), 500));
        this.emblaApi.on('init', debounce(this.handleSwiperInit.bind(this), 100));
        this.emblaApi.on('slidesChanged', debounce(this.handleContentChange.bind(this), 100));
        this.emblaApi.on('scroll', throttle(this.handleSwiperScrolled.bind(this), 500));
        this.cards = this.emblaApi.slideNodes();
        this.numberOfCards = this.cards.length;
        ResizeListener.getInstance().subscribe(this.calculateIfWeHaveToShowControls);
    }
    async slideNext() {
        this.handleNextClick();
    }
    async slidePrev() {
        this.handlePrevClick();
    }
    async getNumberOfCards() {
        return this.numberOfCards;
    }
    getGapCssVariables() {
        let mobileGap = getProperty(this._customGap, 'mobile');
        let tabletGap = getProperty(this._customGap, 'tablet');
        let desktopGap = getProperty(this._customGap, 'desktop');
        let finalMobileGap = mobileGap ? mobileGap : '1rem';
        return {
            [aceSwiperCssVariables.GAP__MOBILE]: finalMobileGap,
            [aceSwiperCssVariables.GAP__TABLET]: tabletGap ? tabletGap : finalMobileGap,
            [aceSwiperCssVariables.GAP__DESKTOP]: desktopGap ? desktopGap : tabletGap ? tabletGap : mobileGap ? mobileGap : '1rem',
        };
    }
    renderLoader() {
        if (this._loading || (!this.hideIfEmpty && this.numberOfCards <= 0)) {
            let loaderCssClass = {
                [this.names.CLASS.CAROUSEL__LOADER]: true,
                [this.names.CLASS.CAROUSEL__LOADER____EMPTY]: this.numberOfCards <= 0
            };
            return (h("div", { class: loaderCssClass }, hasSlot(this.hostElement, aceSwiperSlots.LOADER) ? h("slot", { name: 'loader' }) : h("ace-loader", { show: true })));
        }
    }
    renderSwiperControls() {
        if (this.showControls) {
            const buttonType = this.dark ? 'hidden-dark' : 'hidden';
            return (h("div", { class: this.names.CLASS.TOP__CONTROLS }, h("ace-button", { "icon-name": 'arrow-left', onClick: this.handlePrevClick, type: buttonType, size: 's' }), h("ace-button", { "icon-name": 'arrow-right', onClick: this.handleNextClick, type: buttonType, size: 's' })));
        }
    }
    renderNavigation() {
        const hasLink = this.href && this.href.length > 0;
        /* When there is no "show all" link, only render desktop navigation */
        if (!hasLink) {
            return [h("div", { slot: 'actions' }), h("div", { slot: 'desktop-actions' }, this.renderSwiperControls())];
        }
        return [
            h("ace-button", { slot: 'actions', "icon-name": 'chevron-right', type: 'tertiary', size: 's', href: this.href }),
            h("div", { slot: 'desktop-actions', class: this.names.CLASS.TOP__ACTIONS }, h("ace-button", { type: 'tertiary', size: 's', href: this.href }, getTranslation(this._translations, 'href_button', DEFAULT_TRANSLATIONS)), this.renderSwiperControls())
        ];
    }
    renderHeader() {
        if (!this.headline && !hasSlot(this.hostElement, aceSwiperSlots.HEADLINE_TAG) && !hasSlot(this.hostElement, aceSwiperSlots.INFO_TAG)) {
            return;
        }
        return (h("ace-title-bar", { class: this.names.CLASS.TOP, headline: this.headline, type: 'section', "headline-tag": this.headlineTag }, h("slot", { name: aceSwiperSlots.HEADLINE_TAG, slot: 'headline-tag' }), h("slot", { name: aceSwiperSlots.INFO_TAG, slot: 'info-tag' }), this.renderNavigation()));
    }
    render() {
        let cssClasses = {
            [this.names.CLASS.ROOT]: true,
            [this.names.CLASS.____HIDDEN]: this.hideIfEmpty && this.numberOfCards <= 0
        };
        return (h("div", { key: '8d49db3d414e2f06b3fbcf05e2a064e6792db8c7', id: this.names.ID.ROOT, class: cssClasses, style: this.getGapCssVariables() }, h("slot", { key: '39314e044ab647b3daa510dfb16b3152ec99bedd', name: 'controls' }), this.renderHeader(), this.renderCarousel()));
    }
    static get is() { return "ace-swiper"; }
    static get originalStyleUrls() {
        return {
            "$": ["./ace-swiper.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ace-swiper.css"]
        };
    }
    static get properties() {
        return {
            "headline": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "headline",
                "reflect": false
            },
            "headlineTag": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceHeadlineTag",
                    "resolved": "\"h1\" | \"h2\" | \"h3\" | \"h4\" | \"h5\" | \"h6\"",
                    "references": {
                        "IAceHeadlineTag": {
                            "location": "import",
                            "path": "../ace-headline/ace-headline.types",
                            "id": "src/components/ace-headline/ace-headline.types.ts::IAceHeadlineTag"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "headline-tag",
                "reflect": false,
                "defaultValue": "'h2'"
            },
            "href": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "href",
                "reflect": false
            },
            "autoplay": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "autoplay",
                "reflect": false,
                "defaultValue": "false"
            },
            "delay": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "delay",
                "reflect": false,
                "defaultValue": "5000"
            },
            "fillSpace": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "fill-space",
                "reflect": false,
                "defaultValue": "false"
            },
            "dark": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "dark",
                "reflect": false,
                "defaultValue": "false"
            },
            "translations": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceSwiperTranslations | string",
                    "resolved": "string | { href_button?: string; }",
                    "references": {
                        "IAceSwiperTranslations": {
                            "location": "import",
                            "path": "./ace-swiper.types",
                            "id": "src/components/ace-swiper/ace-swiper.types.ts::IAceSwiperTranslations"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "translations",
                "reflect": false
            },
            "customGap": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceSwiperCustomGap | string",
                    "resolved": "string | { mobile?: string; tablet?: string; desktop?: string; }",
                    "references": {
                        "IAceSwiperCustomGap": {
                            "location": "import",
                            "path": "./ace-swiper.types",
                            "id": "src/components/ace-swiper/ace-swiper.types.ts::IAceSwiperCustomGap"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "custom-gap",
                "reflect": false
            },
            "hideIfEmpty": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "hide-if-empty",
                "reflect": false,
                "defaultValue": "true"
            },
            "loading": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "loading",
                "reflect": false,
                "defaultValue": "false"
            },
            "withControls": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "with-controls",
                "reflect": false,
                "defaultValue": "true"
            }
        };
    }
    static get states() {
        return {
            "showControls": {},
            "numberOfCards": {}
        };
    }
    static get events() {
        return [{
                "method": "slidesMoved",
                "name": "slidesMoved",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "swiperInitialised",
                "name": "swiperInitialised",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "swiperScrolled",
                "name": "swiperScrolled",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "slidesChanged",
                "name": "slidesChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "slideNext": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "slidePrev": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "getNumberOfCards": {
                "complexType": {
                    "signature": "() => Promise<number>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<number>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "hostElement"; }
    static get watchers() {
        return [{
                "propName": "loading",
                "methodName": "parseLoading"
            }, {
                "propName": "customGap",
                "methodName": "parseGap"
            }, {
                "propName": "translations",
                "methodName": "parseTranslations"
            }];
    }
}
//# sourceMappingURL=ace-swiper.js.map
